import Actions from '../actions/actions';
import BotAnalyticsCRUDActions from '../actions/BotAnalyticsCRUDActions';
import {
  BotAnalytics,
  ContactsByPlatformType,
  ContactsBySourceType,
  ContactsGatheredResponseType,
  ContactsSourcesType,
  DemographicAnalyticsCategory,
  DemographicAnalyticsType,
  EmailFilterTabsType,
  EmailStatsPayload,
  QAKeywords,
  TopDialogue,
  XRayAnalyticsPayload,
  XRayAnalyticsType,
} from '../interfaces/botAnalytics';
import SuggestionModel from '../model/SuggestionModel';
import { Pagination } from '../store-interfaces';
import { setBusy, setUnBusy } from './common/commonReducers';

export type BotAnalyticsActionType = {
  type: string;
  topDialogues: {
    dialogues: TopDialogue[];
    pagination: Pagination;
  };
  contactsAmountAnalytics: {
    totalContacts: number[];
    sessions: number[];
    newContacts: number[];
  };
  unansweredQuestions: SuggestionModel[];
  contactsByPlatform: ContactsByPlatformType;
  contactsBySource: ContactsBySourceType;
  qaKeywords: QAKeywords[];
  contactsByPlatformAggregated: ContactsByPlatformType;
  emailAnalyticsByTab: {
    tab: EmailFilterTabsType;
    payload: EmailStatsPayload;
  };
  xRayAnalytics: {
    type: XRayAnalyticsType;
    payload: XRayAnalyticsPayload;
  };
  contactsSources: ContactsSourcesType;
  demographicAnalytics: {
    category: DemographicAnalyticsCategory;
    data: DemographicAnalyticsType;
  };
  contactsGathered: ContactsGatheredResponseType;
};

const initialState: BotAnalytics = {
  busy: false,
  topDialogues: {
    dialogues: [],
    pagination: {
      isLastPage: true,
      page: 0,
      perPage: 0,
      total: 0,
      totalPages: 0,
    },
  },
  contactsAmountAnalytics: {
    totalContacts: [],
    sessions: [],
    totalNewContacts: [],
    date: [],
    messagesCount: [],
  },
  unansweredQuestions: [],
  contactsByPlatform: {
    facebook: [],
    sms: [],
    webChat: [],
    instagram: [],
    totals: {
      facebook: 0,
      sms: 0,
      webChat: 0,
      instagram: 0,
    },
    dates: [],
  },
  contactsBySource: {
    facebookOptIns: 0,
    customerChatWidget: 0,
    messengerAd: 0,
    optInPopups: 0,
    shareLinks: 0,
    commentGuards: 0,
    landingPage: 0,
    contactsCsv: 0,
    adConfig: 0,
    fbPageWelcomer: 0,
    smsKeyword: 0,
    page: 0,
    unknown: 0,
  },
  qaKeywords: [],
  contactsByPlatformAggregated: {
    facebook: [],
    sms: [],
    webChat: [],
    instagram: [],
    totals: {
      facebook: 0,
      sms: 0,
      webChat: 0,
      instagram: 0,
    },
    dates: [],
  },
  emailAnalyticsByTab: {},
  xRayAnalytics: {},
  contactsSources: null,
  demographicAnalytics: null,
  contactsGathered: null,
};

export const botAnalytics = (
  state = initialState,
  action: BotAnalyticsActionType,
) => {
  switch (action.type) {
    case BotAnalyticsCRUDActions.busyAction:
      return setBusy(state);
    case BotAnalyticsCRUDActions.retrieveTopDialoguesAction: {
      const { topDialogues } = action;
      if (topDialogues.pagination.page !== 1) {
        return {
          ...state,
          topDialogues: {
            dialogues: [
              ...state.topDialogues.dialogues,
              ...topDialogues.dialogues,
            ],
            pagination: topDialogues.pagination,
          },
          busy: false,
        };
      }

      return {
        ...state,
        topDialogues: action.topDialogues,
        busy: false,
      };
    }
    case BotAnalyticsCRUDActions.retrieveContactsAmountAnalyticsAction: {
      const { contactsAmountAnalytics } = action;
      return {
        ...state,
        contactsAmountAnalytics,
        busy: false,
      };
    }
    case BotAnalyticsCRUDActions.retrieveUnansweredQuestionsAction: {
      const { unansweredQuestions } = action;
      return {
        ...state,
        unansweredQuestions,
        busy: false,
      };
    }
    case BotAnalyticsCRUDActions.retrieveContactsByPlatformAction: {
      const { contactsByPlatform } = action;
      return {
        ...state,
        contactsByPlatform,
        busy: false,
      };
    }
    case BotAnalyticsCRUDActions.retrieveContactsBySourceAction: {
      const { contactsBySource } = action;
      return {
        ...state,
        contactsBySource,
        busy: false,
      };
    }
    case BotAnalyticsCRUDActions.retrieveQAKeywordsAction: {
      const { qaKeywords } = action;
      return {
        ...state,
        qaKeywords,
        busy: false,
      };
    }
    case BotAnalyticsCRUDActions.retrieveContactsByPlatformAggregatedAction: {
      const { contactsByPlatformAggregated } = action;
      return {
        ...state,
        contactsByPlatformAggregated,
        busy: false,
      };
    }
    case BotAnalyticsCRUDActions.retrieveEmailAnalyticsAction: {
      const { emailAnalyticsByTab } = action;

      return {
        ...state,
        emailAnalyticsByTab: {
          ...state.emailAnalyticsByTab,
          [emailAnalyticsByTab.tab]: emailAnalyticsByTab.payload,
        },
        busy: false,
      };
    }

    case BotAnalyticsCRUDActions.retrieveXRayAnalyticsAction: {
      const { xRayAnalytics } = action;

      return {
        ...state,
        xRayAnalytics: {
          ...state.xRayAnalytics,
          [xRayAnalytics.type]: xRayAnalytics.payload,
        },
        busy: false,
      };
    }

    case BotAnalyticsCRUDActions.retrieveContactsSourcesAnalyticsAction: {
      return {
        ...state,
        contactsSources: action.contactsSources,
        busy: false,
      };
    }

    case BotAnalyticsCRUDActions.retrieveDemographicAnalyticsAction: {
      const { demographicAnalytics } = action;

      return {
        ...state,
        demographicAnalytics: {
          ...state.demographicAnalytics,
          [demographicAnalytics?.category]: demographicAnalytics?.data,
        },
        busy: false,
      };
    }

    case BotAnalyticsCRUDActions.retrieveContactsGatheredAction: {
      const { contactsGathered } = action;

      return {
        ...state,
        contactsGathered,
        busy: false,
      };
    }

    case BotAnalyticsCRUDActions.clearEmailAnalyticsAction: {
      return {
        ...state,
        emailAnalyticsByTab: {},
        busy: false,
      };
    }

    case BotAnalyticsCRUDActions.resetAllAction: {
      return {
        ...initialState,
        busy: false,
      };
    }

    case Actions.UNBUSY:
      return setUnBusy(state);
    default:
      return state;
  }
};
